import { BrDateTimePicker, BrDivider, BrLoading, BrSelect } from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import AnmSelectQuery from 'components/anmSelectQuery/anmSelectQuery';
import CustomConfirmer from 'components/customConfirmer/customConfirmer';
import InfoCircleTooltip from 'components/infoCircleTooltip/infoCircleTooltip';
import {
  EOpcaoExportacaoArquivo,
  getOpcaoExportacaoArquivoDefault,
  OpcaoExportacaoArquivoDetalhe,
} from 'enums/EOpcaoExportacaoArquivo';
import { ESituacaoGuia, TipoSituacaoGuiaDetalhe } from 'enums/ESituacaoGuia';
import { useNotificacao } from 'hooks/useNotificacao';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { IMunicipio } from 'interfaces/IMunicipio';
import { ISubstancia } from 'interfaces/ISubstancia';
import { useBackEndRequests } from 'pages/components/lib/backEndRequests';
import { useBackEndRequests as useBackEndRequestsTA } from 'pages/TitularArrendatario/lib/backEndRequests';
import { useEffect, useState } from 'react';
import { Button, Col, Modal, Radio, Row } from 'react-dsgov';
import { Tooltip } from 'react-tooltip';
import { sfAnd, sfEqual, sfGe, sfLe, sfOr } from 'spring-filter-query-builder';
import { estadosUFOptions } from 'utils/estadosUFLista';
import { converterCompetenciaParaISO } from 'utils/formatCompetencia';
import { formatarNrProcesso } from 'utils/formatNrProcesso';
import { tratarMensagemErro } from 'utils/tratarMensagemErro';
import styles from './modalExportacaoGuias.module.scss';

interface ModalExportacaoGuiasProps {
  readonly title: string;
  readonly isModalOpened: boolean;
  setIsModalOpened: React.Dispatch<React.SetStateAction<boolean>>;
}

interface IFiltrosGuiaRecolhimentoExportacao {
  competenciaInicial: string;
  competenciaFinal: string;
  status: string;
  'processo.numero': string;
  'substanciaCorrelacionada.nome': string;
  'municipio.nome': string;
  'municipio.uf': string;
}

function ModalExportacaoGuias({ title, isModalOpened, setIsModalOpened }: ModalExportacaoGuiasProps) {
  const MAX_DATE = new Date().toLocaleDateString('pt-BR');
  const FIRST_OPTION_LABEL = 'Selecionar';
  const DEFAULT_OPTION_VALUE = '-';
  const PAGE_SIZE = 1000;
  const PAGE_NUMBER = 0;
  const LIMITE_MAXIMO_MESES_INTERVALO_EXPORTACAO = 12;
  const SITUACAO_TEXT_TOOL_TIP = 'As guias com situação "Em preenchimento" não serão exportadas no arquivo.';

  const filtroInicial: IFiltrosGuiaRecolhimentoExportacao = {
    competenciaInicial: '',
    competenciaFinal: '',
    'processo.numero': '',
    'substanciaCorrelacionada.nome': '',
    'municipio.nome': '',
    'municipio.uf': DEFAULT_OPTION_VALUE,
    status: DEFAULT_OPTION_VALUE,
  };

  const [selectedRadio, setSelectedRadio] = useState<string>(getOpcaoExportacaoArquivoDefault());
  const [filtrosGuiaRecolhimento, setFiltrosGuiaRecolhimento] = useState<string>('');
  const [order, setOrder] = useState<string>('status:ASC');
  const [filtros, setFiltros] = useState<IFiltrosGuiaRecolhimentoExportacao>(filtroInicial);
  const [estadoUF, setEstadoUF] = useState<string>(DEFAULT_OPTION_VALUE);
  const [opcoesNrProcessos, setOpcoesNrProcessos] = useState<{ label: string; value: string }[]>([]);
  const [listaNrProcessos, setListaNrProcessos] = useState<string[]>([]);

  const userCpfCnpj = sessionStorage.getItem('userCpfCnpj');

  const { exportarGuiasCSV, exportarGuiasExcel } = useBackEndRequests();
  const { getMunicipiosPorUf, getProcessosMinerarios, getSubstancias } = useBackEndRequestsTA();
  const mostrarNotificacao = useNotificacao();

  const {
    data: listaProcessosData,
    isFetching: isLoadingListaProcessos,
    error: errorListaProcessos,
    refetch: refetchListaProcessos,
  } = useQuery<string[], IErrorWithSentryId>({
    queryKey: ['listaProcessos', userCpfCnpj],
    queryFn: () => getProcessosMinerarios(userCpfCnpj || ''),
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  useEffect(() => {
    setOpcoesNrProcessos(mapProcessosListadosToOpcoes(listaProcessosData || []));
  }, [listaProcessosData]);

  const prepararExportacaoArquivo = async (selectedRadio: string) => {
    const filtrosAplicados = removerFiltrosVazios({ ...filtros });

    if (!validarFiltros(filtrosAplicados)) {
      return;
    }

    const filtrosParaRequisicao = montarRequisicao(filtrosAplicados);
    setFiltrosGuiaRecolhimento(filtrosParaRequisicao.toString());

    await exportarArquivo(selectedRadio, filtrosParaRequisicao);
  };

  const validarFiltros = (filtrosAplicados: IFiltrosGuiaRecolhimentoExportacao) => {
    if (!filtrosAplicados.competenciaInicial && !filtrosAplicados.competenciaFinal) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'As datas das competências são obrigatórias.',
      });
      return false;
    }
    if (!filtrosAplicados.competenciaInicial) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'A data da competência inicial é obrigatória.',
      });
      return false;
    }
    if (!filtrosAplicados.competenciaFinal) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'A data da competência final é obrigatória.',
      });
      return false;
    }

    if (filtrosAplicados.competenciaInicial && filtrosAplicados.competenciaFinal) {
      const dataCompetenciaInicial = new Date(converterCompetenciaParaISO(filtrosAplicados.competenciaInicial));
      const dataCompetenciaFinal = new Date(converterCompetenciaParaISO(filtrosAplicados.competenciaFinal));
      if (dataCompetenciaInicial > dataCompetenciaFinal) {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Data da competência inicial não pode ser maior que a data da competência final.',
        });
        return false;
      }

      const diferencaMeses =
        (dataCompetenciaFinal.getFullYear() - dataCompetenciaInicial.getFullYear()) * 12 +
        (dataCompetenciaFinal.getMonth() - dataCompetenciaInicial.getMonth());
      if (diferencaMeses > LIMITE_MAXIMO_MESES_INTERVALO_EXPORTACAO) {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: `O intervalo entre competência inicial e final não pode ser superior a ${LIMITE_MAXIMO_MESES_INTERVALO_EXPORTACAO} meses.`,
        });
        return false;
      }
    }
    return true;
  };

  const validarOpcaoArquivo = (selectedRadio: string) => {
    const opcaoSelecionada = selectedRadio as EOpcaoExportacaoArquivo;

    if (!Object.values(EOpcaoExportacaoArquivo).includes(opcaoSelecionada)) {
      return false;
    }

    return OpcaoExportacaoArquivoDetalhe[opcaoSelecionada].isHabilitado;
  };

  const removerFiltrosVazios = (filtros: IFiltrosGuiaRecolhimentoExportacao) => {
    Object.keys(filtros).forEach((key) => {
      if (
        filtros[key as keyof IFiltrosGuiaRecolhimentoExportacao] === '' ||
        filtros[key as keyof IFiltrosGuiaRecolhimentoExportacao] == DEFAULT_OPTION_VALUE
      ) {
        delete filtros[key as keyof IFiltrosGuiaRecolhimentoExportacao];
      }
    });
    return filtros;
  };

  const limparFiltros = () => {
    setFiltros(filtroInicial);
    setEstadoUF(DEFAULT_OPTION_VALUE);
    setFiltrosGuiaRecolhimento('');
    setListaNrProcessos([]);
  };

  const montarRequisicao = (filtros: IFiltrosGuiaRecolhimentoExportacao) => {
    const itemFilters: string[] = [];
    Object.entries(filtros).forEach(([key, value]) => {
      if (value) {
        if (key === 'competenciaInicial') {
          const competenciaFormatada = converterCompetenciaParaISO(value);
          itemFilters.push(sfGe('competencia', competenciaFormatada).toString());
          return;
        }
        if (key === 'competenciaFinal') {
          const competenciaFormatada = converterCompetenciaParaISO(value);
          itemFilters.push(sfLe('competencia', competenciaFormatada).toString());
          return;
        }

        if (key === 'status') {
          // Os 3 status serão considerados todos como VENCIDO
          if (value === TipoSituacaoGuiaDetalhe.Vencida.nome) {
            const situacaoGuiaVencida = sfOr([
              sfEqual('status', TipoSituacaoGuiaDetalhe[ESituacaoGuia.CANCELADO].nome),
              sfEqual('status', TipoSituacaoGuiaDetalhe[ESituacaoGuia.VENCIDO].nome),
              sfEqual('status', TipoSituacaoGuiaDetalhe[ESituacaoGuia.REJEITADO].nome),
            ]).toString();
            itemFilters.push(situacaoGuiaVencida);
            return;
          }
        }
        itemFilters.push(sfEqual(key, value.toString().toUpperCase()).toString());
      }
    });

    // Caso haja algum processo selecionado - incluir iteração para realização da query
    if (listaNrProcessos.length > 0) {
      const processosFilters: string[] = [];
      listaNrProcessos.forEach((nrProcesso) => {
        const [numero, ano] = nrProcesso.toString().replaceAll('.', '').split('/');
        processosFilters.push(
          sfAnd([sfEqual('processo.numero', numero).toString(), sfEqual('processo.ano', ano).toString()]).toString()
        );
      });

      itemFilters.push(sfOr(processosFilters).toString());
    }

    const filtroMontado = sfAnd(itemFilters).toString();

    return filtroMontado;
  };

  const onChangeFiltro = (campo: keyof IFiltrosGuiaRecolhimentoExportacao, valor: string | number | Date) => {
    setFiltros((prevFiltros) => ({
      ...prevFiltros,
      [campo]: valor,
    }));
  };

  const getFileNameFromContentDisposition = (contentDisposition?: string) => {
    if (!contentDisposition) {
      return 'guias_recolhimento.csv';
    }

    const utf8FileNameMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8FileNameMatch?.[1]) {
      return decodeURIComponent(utf8FileNameMatch[1].replace(/"/g, ''));
    }

    const fileNameMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
    if (fileNameMatch?.[1]) {
      return fileNameMatch[1].replace(/"/g, '').trim();
    }

    return 'guias_recolhimento.csv';
  };

  const getContentDispositionHeader = (headers: unknown, request?: unknown) => {
    if (headers && typeof headers === 'object') {
      if ('get' in headers && typeof headers.get === 'function') {
        const headerValue = headers.get('content-disposition') || headers.get('Content-Disposition');
        if (headerValue) {
          return headerValue;
        }
      }

      const headerMap = headers as Record<string, string | undefined>;
      const headerValue = headerMap['content-disposition'] || headerMap['Content-Disposition'];
      if (headerValue) {
        return headerValue;
      }
    }

    if (request && typeof request === 'object' && 'getResponseHeader' in request) {
      const xhrRequest = request as { getResponseHeader: (name: string) => string | null };

      return (
        xhrRequest.getResponseHeader('content-disposition') ||
        xhrRequest.getResponseHeader('Content-Disposition') ||
        undefined
      );
    }

    return undefined;
  };

  const getPdfTooltipText = () => {
    return 'Opção indisponível - Esta opção será disponibilizada na versão 2.0.';
  };

  const getOpcaoExportacaoIconClass = (opcao: EOpcaoExportacaoArquivo) => {
    switch (opcao) {
      case EOpcaoExportacaoArquivo.EXCEL:
        return `fas fa-file-excel ${styles.iconExcel}`;
      case EOpcaoExportacaoArquivo.PDF:
        return `fas fa-file-pdf ${styles.iconPdf}`;
      case EOpcaoExportacaoArquivo.CSV:
      default:
        return `fas fa-file-csv ${styles.icon}`;
    }
  };

  const baixarArquivo = (response: AxiosResponse<BlobPart>) => {
    const contentDisposition = getContentDispositionHeader(response.headers, response.request);
    const fileName = getFileNameFromContentDisposition(contentDisposition);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const concluirExportacaoComSucesso = () => {
    mostrarNotificacao({
      tipo: 'success',
      conteudo: 'Arquivo gerado com sucesso!',
    });
    setIsModalOpened(false);
  };

  const exportarArquivo = async (selectedRadio: string, filtro: string) => {
    if (!validarOpcaoArquivo(selectedRadio)) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Selecione uma opção de exportação válida.',
      });
      return;
    }

    switch (selectedRadio) {
      case EOpcaoExportacaoArquivo.CSV:
        await exportarGuiasCSV(filtro, PAGE_NUMBER, PAGE_SIZE, order)
          .then((response) => {
            baixarArquivo(response);
            concluirExportacaoComSucesso();
          })
          .catch((error) => {
            mostrarNotificacao({
              tipo: 'danger',
              conteudo: tratarMensagemErro(
                'exportarGuiasCSV',
                error,
                'Erro ao exportar guias de recolhimento. Tente novamente mais tarde.'
              ),
              complementoSentryId: error.sentryId,
            });
          });
        break;
      case EOpcaoExportacaoArquivo.EXCEL:
        await exportarGuiasExcel(filtro, PAGE_NUMBER, PAGE_SIZE, order)
          .then((response) => {
            baixarArquivo(response);
            concluirExportacaoComSucesso();
          })
          .catch((error) => {
            mostrarNotificacao({
              tipo: 'danger',
              conteudo: tratarMensagemErro(
                'exportarGuiasExcel',
                error,
                'Erro ao exportar guias de recolhimento. Tente novamente mais tarde.'
              ),
              complementoSentryId: error.sentryId,
            });
          });
        break;
    }
  };

  const mapProcessosListadosToOpcoes = (listaProcessos: string[]) => {
    if (!listaProcessos || !Array.isArray(listaProcessos)) {
      return [];
    }
    return listaProcessos.map((processo) => ({
      label: formatarNrProcesso(processo),
      value: formatarNrProcesso(processo),
    }));
  };

  return (
    <div className={styles.modalOpcoesExportacaoContainer}>
      <Modal
        title={title}
        useScrim
        showCloseButton
        onCloseButtonClick={() => {
          setIsModalOpened(false);
        }}
        modalOpened={isModalOpened}
        className={styles.modal}
      >
        <div className={styles.contentContainer}>
          <div className={styles.subtitle}>
            <span>Filtros:</span>
          </div>
          <form autoComplete='off'>
            <Row>
              <Col>
                <BrDateTimePicker
                  label='Competência Inicial'
                  name='competenciaInicial'
                  dataMode='single'
                  dataType='month'
                  maxDate={MAX_DATE}
                  value={filtros?.['competenciaInicial']}
                  onChange={(e) => onChangeFiltro('competenciaInicial', e)}
                />
              </Col>
              <Col>
                <BrDateTimePicker
                  label='Competência Final'
                  name='competenciaFinal'
                  dataMode='single'
                  dataType='month'
                  maxDate={MAX_DATE}
                  value={filtros?.['competenciaFinal']}
                  onChange={(e) => onChangeFiltro('competenciaFinal', e)}
                />
              </Col>
              <Col>
                <div className={styles.situacaoSelectContainer}>
                  <BrSelect
                    key={'situacaoSelect' + filtros?.['status']}
                    label='Situação'
                    options={[
                      { label: FIRST_OPTION_LABEL, value: DEFAULT_OPTION_VALUE },
                      ...Object.values(TipoSituacaoGuiaDetalhe)
                        .filter((situacao) => situacao.apresentarOpcaoComoFiltroParaExportacao)
                        .map((situacao) => ({
                          label: situacao.label,
                          value: situacao.nome,
                        })),
                    ]}
                    value={filtros?.['status']}
                    onChange={(value: string) => onChangeFiltro('status', value)}
                  />
                  <div className={styles.situacaoTooltipContainer}>
                    <InfoCircleTooltip text={SITUACAO_TEXT_TOOL_TIP} id='situacao-select-tooltip' customPlace='right' />
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col>
                {listaProcessosData && !isLoadingListaProcessos && !errorListaProcessos && (
                  <BrSelect
                    label={'Processo Minerário'}
                    id={'id-select-processos-a-declarar'}
                    data-testid={'id-select-processos-a-declarar'}
                    key={opcoesNrProcessos?.map((opcao) => opcao?.value).join('-')}
                    placeholder={
                      listaNrProcessos.length === 0 ? 'Digite e selecione um processo...' : 'Selecione os processos'
                    }
                    value={listaNrProcessos}
                    onClick={(e) => {
                      if (e.target instanceof HTMLInputElement && e.target.ariaLabel === 'Selecionar todos') {
                        const idSelectAllInput = e.target.id;
                        const labelSelectAll = document.querySelector(`label[for="${idSelectAllInput}"]`);
                        if (labelSelectAll) {
                          if (e.target.checked) {
                            setListaNrProcessos(opcoesNrProcessos?.map((opcao) => opcao?.value) ?? []);
                          } else {
                            setListaNrProcessos([]);
                          }
                        }
                      }
                    }}
                    onChange={(value: string[] | null) => {
                      setListaNrProcessos(value ?? []);
                    }}
                    options={opcoesNrProcessos}
                    disabled={false}
                    type={'multiple'}
                  />
                )}
                {isLoadingListaProcessos && (
                  <div data-testid='loading'>
                    <BrLoading />
                  </div>
                )}
                {errorListaProcessos && (
                  <div className={styles.listaSuspensaContainer}>
                    <div className={styles.selectErro}>
                      <BrSelect
                        label={'Processo Minerário'}
                        id={'id-select-processos-a-declarar'}
                        data-testid={'id-select-processos-a-declarar'}
                        placeholder='Erro ao carregar processos'
                        value={[]}
                        options={[]}
                        disabled
                      />
                      <span className={styles.errorText}>Falha na consulta de Processo</span>
                      <span
                        className={styles.linkText}
                        onClick={() => refetchListaProcessos()}
                        onKeyDown={() => refetchListaProcessos()}
                      >
                        (Refazer consulta)
                      </span>
                    </div>
                    <div className={styles.btn}></div>
                  </div>
                )}
              </Col>
              <Col>
                <AnmSelectQuery
                  label='Substância'
                  value={filtros?.['substanciaCorrelacionada.nome']}
                  keyQuery={['getSubstancias']}
                  functionQuery={() => getSubstancias()}
                  formatResult={(result: ISubstancia[]) =>
                    result.map((substanciaCorrelacionada: ISubstancia) => ({
                      label: substanciaCorrelacionada.nome,
                      value: substanciaCorrelacionada.nome,
                    }))
                  }
                  setValue={(value: string) => onChangeFiltro('substanciaCorrelacionada.nome', value)}
                  defaultValue={FIRST_OPTION_LABEL}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <BrSelect
                  key={'estadoUFSelect' + estadoUF}
                  label='Estado (UF)'
                  options={[{ label: 'Selecionar', value: '-' }, ...estadosUFOptions]}
                  value={estadoUF}
                  onChange={(value: string) => setEstadoUF(value)}
                />
              </Col>
              <Col>
                <AnmSelectQuery
                  key={'municipioSelect' + estadoUF}
                  label='Município'
                  value={filtros?.['municipio.nome']}
                  keyQuery={['getMunicipiosPorUf']}
                  functionQuery={() =>
                    estadoUF && estadoUF !== DEFAULT_OPTION_VALUE ? getMunicipiosPorUf(estadoUF) : Promise.resolve([])
                  }
                  formatResult={(result: IMunicipio[]) =>
                    result.map((municipio: IMunicipio) => ({
                      label: municipio.nome,
                      value: municipio.nome,
                    }))
                  }
                  setValue={(value: string) => onChangeFiltro('municipio.nome', value)}
                  isDisabled={!estadoUF || estadoUF === DEFAULT_OPTION_VALUE}
                  defaultValue={FIRST_OPTION_LABEL}
                />
              </Col>
            </Row>
          </form>
          <div className={styles.divider}>
            <BrDivider />
          </div>

          <div className={styles.radioContainer}>
            <div className={styles.radioContainerTitle}>
              <span>Selecione uma opção para exportação do arquivo:</span>
            </div>
            <div className={styles.radioContainerOptions}>
              {(Object.values(EOpcaoExportacaoArquivo) as EOpcaoExportacaoArquivo[]).map((opcao) => {
                const detalheOpcao = OpcaoExportacaoArquivoDetalhe[opcao];
                const radioId = `radio-${opcao}`;
                const tooltipId = `${radioId}-tooltip`;
                const isDisabled = !detalheOpcao.isHabilitado;

                return (
                  <div key={opcao} data-tooltip-id={isDisabled ? tooltipId : undefined}>
                    <Radio
                      checked={selectedRadio === opcao}
                      id={radioId}
                      label={
                        <div className={styles.radioContent}>
                          <i className={getOpcaoExportacaoIconClass(opcao)} />
                          <span className={styles.radioLabel}>{detalheOpcao.nome}</span>
                        </div>
                      }
                      data-testid={radioId}
                      value={opcao}
                      name='radio-items'
                      className={`${styles.radio} ${selectedRadio === opcao ? styles.selected : ''}`}
                      onClick={() => setSelectedRadio(opcao)}
                      disabled={isDisabled}
                    />
                    {isDisabled && (
                      <Tooltip
                        opacity={1}
                        id={tooltipId}
                        place='bottom'
                        className={styles.tooltip}
                        variant='warning'
                        delayHide={100}
                      >
                        <div className={styles.tooltipText}>{getPdfTooltipText()}</div>
                      </Tooltip>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div>
          <div className={styles.botao}>
            <Button
              secondary
              small
              m={2}
              onClick={() => {
                setIsModalOpened(false);
              }}
            >
              Cancelar
            </Button>
            <Button secondary small m={2} onClick={limparFiltros}>
              Limpar todos filtros
            </Button>
            <div data-tooltip-id='gerar-arrecadacao-tooltip'>
              <CustomConfirmer
                disabled={selectedRadio == ''}
                asyncCall={() => prepararExportacaoArquivo(selectedRadio)}
              >
                Exportar Arquivo
              </CustomConfirmer>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default ModalExportacaoGuias;
